/* eslint-disable react/require-default-props */
import React, { useEffect, useRef } from 'react';
import { useState } from 'react';
import { MdRadioButtonChecked, MdRadioButtonUnchecked } from 'react-icons/md';
import ReactSelect, {
  OptionsType,
  OptionTypeBase,
  ValueType,
} from 'react-select';

import { useField } from 'formik';

import Button from '~/components/Button';
import Modal from '~/components/Modal';
import colors from '~/styles/colors';

import { SelectProps } from '..';

import customStyles from '../customStyles';
import { Container as SelectContainer, LabelContainer } from '../styles';
import { Container, ErrorSpan, Option, OptionList } from './styles';

export type SelectModalProps = SelectProps;

export type SelectModalOption = ValueType<OptionTypeBase>;

function SelectModal({
  name,
  label,
  options,
  optional,
  isDisabled,
  isRequired,
  ...rest
}: SelectModalProps) {
  const [modal, setModal] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<SelectModalOption>();

  const selectRef = useRef(null);

  const [field, meta, helpers] = useField<SelectModalOption>(name);
  const { value } = field;
  const { error, initialValue } = meta;
  const { setValue } = helpers;

  const handleSubmitOption = () => {
    setValue(selectedOption);
    setModal(false);
  };

  const isMulti = (
    data: SelectModalOption,
  ): data is OptionsType<OptionTypeBase> => {
    return Object.prototype.hasOwnProperty.call(data, 'values');
  };

  const hasOption = (optionValue: unknown): boolean => {
    if (selectedOption) {
      if (isMulti(selectedOption)) {
        return selectedOption.some((option) => option.value === optionValue);
      }
      return selectedOption.value === optionValue;
    }
    return false;
  };

  useEffect(() => {
    setSelectedOption(value);
  }, [value]);

  useEffect(() => {
    const option = options?.find((option) => option.value === initialValue);

    setValue(option);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  return (
    <Container>
      <SelectContainer hasLabel={!!label}>
        {label && (
          <LabelContainer isRequired={isRequired}>
            <label htmlFor={name}>{label}</label>
            {optional && <small>(Opcional)</small>}
          </LabelContainer>
        )}
        <ReactSelect
          noOptionsMessage={() => 'Não há opções'}
          ref={selectRef}
          classNamePrefix="react-select"
          options={options}
          menuPlacement="auto"
          menuPortalTarget={document.body}
          styles={{
            ...customStyles,
            menuPortal: () => ({ display: 'none' }),
          }}
          isDisabled={isDisabled}
          onMenuOpen={() => !isDisabled && setModal(true)}
          value={value}
          onChange={(newValue) => {
            setValue(newValue);
          }}
          {...rest}
        />
        {error && <ErrorSpan>{error}</ErrorSpan>}
      </SelectContainer>

      {modal && (
        <Modal
          title={label}
          open={modal}
          modalState={(opened) => setModal(opened)}
          disableScrollBar
        >
          <OptionList>
            {options &&
              options?.length > 0 &&
              options?.map((option) => (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <Option
                  key={option.value}
                  onClick={() =>
                    setSelectedOption({
                      label: option.label,
                      value: option.value,
                    })
                  }
                >
                  {hasOption(option.value) ? (
                    <MdRadioButtonChecked size={24} color={colors.primary} />
                  ) : (
                    <MdRadioButtonUnchecked size={24} color="#b4b4b4" />
                  )}
                  <label htmlFor={`selectModal_${name}`}>{option.label}</label>
                </Option>
              ))}
          </OptionList>

          <Button
            type="button"
            onClick={handleSubmitOption}
            disabled={!selectedOption}
          >
            Confirmar seleção
          </Button>
        </Modal>
      )}
    </Container>
  );
}

export default SelectModal;
